/*
 * Naturlabs listing behaviour: collection, search results and list collections. Loaded with defer by the
 * sections that need it (several sections may include it: the guard below defines everything once).
 *
 * <listing-results data-section-id data-url>
 *   Filters (desktop sidebar form + mobile drawer form), price range slider, sort dropdown, active filter chips,
 *   pagination links, load more and search tabs. Every change fetches the section through the Section Rendering
 *   API (GET {url}&section_id={id}), swaps the regions marked [data-listing-region="name"] and pushes the URL.
 *   Requests are abortable, responses cached per URL, back / forward re-render, errors fall back to a full load.
 *   Without JavaScript every control is a plain GET form or a link.
 *   Emits `listing:updated` { url, sectionId } on the host after every render.
 *
 *   Client mode (data-filter-mode="client", snippets/nl-filter-client.liquid): the whole small catalogue is on the
 *   page and [data-client-facets] filter it in place: OR inside a group, AND across groups, live option counts
 *   (options that would give no result are disabled), chips, "Vymazat všechny filtry", the product count and the
 *   drawer button "Zobrazit X produktů". State lives in the URL (?kat=imunita,zaklad&davkovani=...) through
 *   history.replaceState and survives sorting (server re-render) and reloads.
 *
 * <search-tab-counts data-endpoint>
 *   Fills empty [data-tab-count="all|product|article|page"] slots from sections/nl-search-counts.liquid.
 *
 * <nl-read-more>
 *   Clamps [data-read-more-content] (max-height from --read-more-height) and shows [data-read-more-toggle] only when
 *   the content overflows; smooth height animation, aria-expanded kept in sync.
 *
 * Motion: new grid items get data-reveal (nl-motion.js reveals them in a left to right stagger). Everything
 * respects prefers-reduced-motion and the theme motion setting through NL.motion.reduced.
 */
(() => {
  'use strict';

  if (customElements.get('listing-results')) return;

  const NL = (window.NL = window.NL || {});
  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };
  const reduced = () =>
    NL.motion ? NL.motion.reduced : window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  /* ------------------------------------------------------------------
   * URL helpers and response cache
   * ------------------------------------------------------------------ */
  const CACHE_LIMIT = 30;
  const cache = new Map();

  const remember = (key, html) => {
    cache.set(key, html);
    if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
  };

  // Path + query without section_id or hash, used for history and as the cache key base
  const cleanUrl = (input) => {
    const url = new URL(input, window.location.origin);
    url.searchParams.delete('section_id');
    const query = url.searchParams.toString();
    return url.pathname + (query ? `?${query}` : '');
  };

  const sectionUrl = (pageUrl, sectionId) => {
    const url = new URL(pageUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.pathname + url.search;
  };

  const fetchHTML = async (url, signal) => {
    const cached = cache.get(url);
    if (cached) return cached;
    const response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = await response.text();
    remember(url, html);
    return html;
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  /* ==========================================================================
     <listing-results>
     ========================================================================== */

  class ListingResults extends HTMLElement {
    constructor() {
      super();
      this.onPopState = this.onPopState.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.formTimer = 0;
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.current = cleanUrl(window.location.href);
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('popstate', this.onPopState);

      if (!this._bound) {
        this._bound = true;
        this.addEventListener('change', (event) => this.onChange(event));
        this.addEventListener('input', (event) => this.onInput(event));
        this.addEventListener('submit', (event) => this.onSubmit(event));
        this.addEventListener('click', (event) => this.onClick(event));
        this.addEventListener('keydown', (event) => this.onKeydown(event));
        this.addEventListener('focusout', (event) => this.onFocusOut(event));
        this.addEventListener('toggle', (event) => this.onToggle(event), true);
        this.addEventListener('dialog:close', () => this.onDrawerClose());
      }

      this.clientInit();

      if (!designMode() && !(window.history.state && window.history.state.nlListing)) {
        try {
          window.history.replaceState({ ...(window.history.state || {}), nlListing: true }, '', window.location.href);
        } catch (error) {
          /* history not available (sandboxed preview) */
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('popstate', this.onPopState);
      window.clearTimeout(this.formTimer);
      if (this.controller) this.controller.abort();
    }

    /* ---------------- Events ---------------- */

    onChange(event) {
      const target = event.target;
      const clientForm = target.closest('[data-client-facets]');
      if (clientForm) {
        this.clientChange(target, clientForm);
        return;
      }
      const form = target.closest('[data-facets-form]');
      if (!form) return;
      if (target.matches('[data-range]')) this.rangeToInputs(target);
      if (target.matches('[data-price-input]')) {
        this.orderPrices(target);
        this.inputsToRange(target);
      }
      // Arrow keys on a slider fire a change per step: wait for the user to settle
      this.queueForm(form, target.matches('[data-range]') ? 400 : 0);
    }

    onInput(event) {
      const target = event.target;
      if (target.matches('[data-range]')) {
        this.rangeToInputs(target);
        return;
      }
      if (target.matches('[data-price-input]')) {
        this.inputsToRange(target);
        const form = target.closest('[data-facets-form]');
        if (form) this.queueForm(form, 900);
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (form.matches('[data-client-facets]')) {
        event.preventDefault();
        return;
      }
      if (form.matches('[data-facets-form]')) {
        event.preventDefault();
        this.queueForm(form, 0);
        return;
      }
      if (form.matches('[data-sort-form]')) {
        event.preventDefault();
        const submitter = event.submitter || this.lastSortButton;
        if (submitter && submitter.name === 'sort_by') this.applySort(submitter.value);
      }
    }

    onClick(event) {
      const sortButton = event.target.closest('.nl-sort__option');
      if (sortButton) this.lastSortButton = sortButton;

      const clientRemove = event.target.closest('[data-client-remove]');
      if (clientRemove && this.client) {
        const [group, value] = clientRemove.dataset.clientRemove.split('~');
        if (this.client.state[group]) this.client.state[group].delete(value);
        if (clientRemove.closest('dialog')) this.drawerDirty = true;
        this.clientCommit({ focusAfter: true });
        return;
      }
      const clientClear = event.target.closest('[data-client-clear]');
      if (clientClear && this.client) {
        this.client.state = {};
        if (clientClear.closest('dialog')) this.drawerDirty = true;
        this.clientCommit({ focusAfter: true });
        return;
      }

      const more = event.target.closest('[data-facet-more]');
      if (more) {
        const group = more.closest('[data-facet-group]');
        if (group) this.setGroupExpanded(group, !group.hasAttribute('data-expanded'));
        return;
      }

      const loadMore = event.target.closest('[data-load-more]');
      if (loadMore) {
        if (this.isModified(event)) return;
        event.preventDefault();
        this.loadMore(loadMore);
        return;
      }

      const link = event.target.closest('a[data-listing-link], .nl-pagination a[href]');
      if (link && this.contains(link)) {
        if (this.isModified(event)) return;
        event.preventDefault();
        if (link.closest('dialog')) this.drawerDirty = true;
        const isPage = Boolean(link.closest('.nl-pagination'));
        this.render(link.href, {
          full: link.hasAttribute('data-listing-full'),
          scroll: isPage,
          focusTab: link.hasAttribute('data-listing-full'),
        });
      }
    }

    onKeydown(event) {
      const details = event.target.closest('[data-sort]');
      if (!details) return;
      const summary = details.querySelector('summary');
      const options = Array.from(details.querySelectorAll('.nl-sort__option'));

      if (event.key === 'Escape' && details.open) {
        event.preventDefault();
        event.stopPropagation();
        details.open = false;
        summary.focus();
        return;
      }

      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || !options.length) return;
      event.preventDefault();

      if (!details.open) {
        details.open = true;
        const current = options.find((option) => option.getAttribute('aria-current') === 'true') || options[0];
        current.focus();
        return;
      }

      const index = options.indexOf(document.activeElement);
      const last = options.length - 1;
      let next = 0;
      if (event.key === 'End') next = last;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'ArrowDown') next = index < 0 || index === last ? 0 : index + 1;
      else next = index <= 0 ? last : index - 1;
      options[next].focus();
    }

    // Keyboard focus leaving the open dropdown closes it. Pointer clicks outside are handled by onDocumentClick
    // (Safari does not focus buttons on click, so a null relatedTarget must not close the menu mid click).
    onFocusOut(event) {
      const details = event.target.closest('[data-sort]');
      if (!details || !details.open || !event.relatedTarget) return;
      if (!details.contains(event.relatedTarget)) details.open = false;
    }

    onToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-sort]')) return;
      if (details.open) {
        this.querySelectorAll('[data-sort][open]').forEach((other) => {
          if (other !== details) other.open = false;
        });
      }
    }

    onDocumentClick(event) {
      this.querySelectorAll('[data-sort][open]').forEach((details) => {
        if (!details.contains(event.target)) details.open = false;
      });
    }

    onPopState() {
      const url = cleanUrl(window.location.href);
      if (url === this.current) return;
      this.render(url, { push: false });
    }

    // After filtering in the mobile drawer, bring the (possibly shorter) results back into view
    onDrawerClose() {
      if (!this.drawerDirty) return;
      this.drawerDirty = false;
      const main = this.querySelector('.nl-listing__main');
      if (!main) return;
      const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nl-header-offset')) || 0;
      const top = main.getBoundingClientRect().top;
      if (top < offset || top > window.innerHeight) {
        window.scrollTo({ top: Math.max(0, window.scrollY + top - offset - 12), behavior: reduced() ? 'auto' : 'smooth' });
      }
    }

    isModified(event) {
      return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0;
    }

    /* ---------------- Filters ---------------- */

    queueForm(form, delay) {
      window.clearTimeout(this.formTimer);
      if (form.dataset.context === 'drawer') this.drawerDirty = true;
      this.formTimer = window.setTimeout(() => {
        const query = this.formQuery(form);
        const base = this.dataset.url || window.location.pathname;
        this.render(query ? `${base}?${query}` : base, { source: form.dataset.context });
      }, delay);
    }

    formQuery(form) {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (typeof value !== 'string') return;
        const trimmed = value.trim();
        if (trimmed !== '') params.append(key, trimmed);
      });
      // Price inputs equal to the range bounds add nothing: keep URLs clean
      form.querySelectorAll('[data-price-input]').forEach((input) => {
        const value = input.value.trim();
        if (value !== '' && input.dataset.bound !== undefined && Number(value) === Number(input.dataset.bound)) {
          params.delete(input.name);
        }
      });
      return params.toString();
    }

    setGroupExpanded(group, expanded) {
      group.toggleAttribute('data-expanded', expanded);
      const button = group.querySelector('[data-facet-more]');
      if (!button) return;
      button.setAttribute('aria-expanded', String(expanded));
      const more = button.querySelector('[data-more-label]');
      const less = button.querySelector('[data-less-label]');
      if (more) more.hidden = expanded;
      if (less) less.hidden = !expanded;
    }

    /* Price range: two number inputs (submitted) + two range inputs (visual, not submitted) */
    priceParts(element) {
      const wrap = element.closest('[data-price-range]');
      if (!wrap) return null;
      const minRange = wrap.querySelector('[data-range="min"]');
      const maxRange = wrap.querySelector('[data-range="max"]');
      const minInput = wrap.querySelector('[data-price-input="min"]');
      const maxInput = wrap.querySelector('[data-price-input="max"]');
      const top = Number((maxRange && maxRange.max) || (maxInput && maxInput.dataset.bound) || 0);
      return { wrap, minRange, maxRange, minInput, maxInput, top };
    }

    paintRange(parts, min, max) {
      if (!parts.top) return;
      parts.wrap.style.setProperty('--from', `${((min / parts.top) * 100).toFixed(2)}%`);
      parts.wrap.style.setProperty('--to', `${((max / parts.top) * 100).toFixed(2)}%`);
      // Keep the min thumb grabbable when both thumbs meet at the right end
      if (parts.minRange) parts.minRange.style.zIndex = min >= parts.top * 0.96 ? '3' : '';
    }

    rangeToInputs(range) {
      const parts = this.priceParts(range);
      if (!parts || !parts.minRange || !parts.maxRange) return;
      let min = Number(parts.minRange.value);
      let max = Number(parts.maxRange.value);
      if (min > max) {
        if (range === parts.minRange) {
          min = max;
          parts.minRange.value = String(min);
        } else {
          max = min;
          parts.maxRange.value = String(max);
        }
      }
      if (parts.minInput) parts.minInput.value = min <= 0 ? '' : String(min);
      if (parts.maxInput) parts.maxInput.value = max >= parts.top ? '' : String(max);
      this.paintRange(parts, min, max);
    }

    inputsToRange(input) {
      const parts = this.priceParts(input);
      if (!parts) return;
      const min = parts.minInput && parts.minInput.value !== '' ? clamp(Number(parts.minInput.value), 0, parts.top) : 0;
      const max =
        parts.maxInput && parts.maxInput.value !== '' ? clamp(Number(parts.maxInput.value), 0, parts.top) : parts.top;
      if (parts.minRange) parts.minRange.value = String(Math.min(min, max));
      if (parts.maxRange) parts.maxRange.value = String(Math.max(min, max));
      this.paintRange(parts, Math.min(min, max), Math.max(min, max));
    }

    orderPrices(input) {
      const parts = this.priceParts(input);
      if (!parts || !parts.minInput || !parts.maxInput) return;
      const min = parts.minInput.value;
      const max = parts.maxInput.value;
      if (min !== '' && max !== '' && Number(min) > Number(max)) {
        parts.minInput.value = max;
        parts.maxInput.value = min;
      }
    }

    /* ---------------- Sort ---------------- */

    applySort(value) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      if (value) url.searchParams.set('sort_by', value);
      else url.searchParams.delete('sort_by');
      this.querySelectorAll('[data-sort-input]').forEach((input) => {
        input.value = value;
      });
      this.querySelectorAll('[data-sort][open]').forEach((details) => {
        details.open = false;
      });
      this.render(url.toString(), { focusSort: true });
    }

    /* ---------------- Rendering ---------------- */

    regionNames(root) {
      return Array.from(root.querySelectorAll('[data-listing-region]'))
        .map((region) => region.dataset.listingRegion)
        .join('|');
    }

    setBusy(state) {
      this.toggleAttribute('data-busy', state);
      const grid = this.querySelector('[data-listing-region="grid"]');
      if (grid) grid.setAttribute('aria-busy', String(state));
    }

    async render(url, options = {}) {
      const pageUrl = cleanUrl(url);
      const requestUrl = sectionUrl(pageUrl, this.sectionId);
      window.clearTimeout(this.formTimer);
      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setBusy(true);

      try {
        const html = await fetchHTML(requestUrl, controller.signal);
        if (controller.signal.aborted) return;
        const fresh = parse(html).querySelector('listing-results');
        if (!fresh) throw new Error('Listing markup missing');

        if (options.push !== false && pageUrl !== this.current && !designMode()) {
          window.history.pushState({ nlListing: true }, '', pageUrl);
        }
        this.current = pageUrl;

        if (options.full || this.regionNames(fresh) !== this.regionNames(this)) {
          this.replaceWhole(fresh, options);
          return;
        }

        const freshCount = fresh.querySelector('[data-listing-region="count"]');
        const countText = freshCount ? freshCount.textContent : '';
        this.removeAttribute('data-appended');
        this.swap(fresh);
        this.afterRender(countText, options);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        window.location.assign(pageUrl);
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.setBusy(false);
        }
      }
    }

    // New grid items animate in (the first row is rendered without data-reveal for the LCP on page load)
    prepareReveal(root) {
      if (reduced()) return;
      root.querySelectorAll('[data-listing-item]:not([data-reveal])').forEach((item, index) => {
        item.setAttribute('data-reveal', '');
        item.setAttribute('data-reveal-delay', String((index % 4) * 70));
      });
    }

    swap(fresh) {
      const active = document.activeElement;
      const activeInside = active && active !== document.body && this.contains(active);
      const focus = activeInside
        ? {
            id: active.id || null,
            text: active.matches('input[type="number"], input[type="text"], input[type="search"]') ? active.value : null,
            dialog: active.closest('dialog[open]'),
          }
        : null;
      const expanded = Array.from(this.querySelectorAll('[data-facet-group][data-expanded]')).map((group) => group.id);

      this.querySelectorAll('[data-listing-region]').forEach((region) => {
        const name = region.dataset.listingRegion;
        const next = fresh.querySelector(`[data-listing-region="${name}"]`);
        if (!next) return;
        if (name === 'grid') this.prepareReveal(next);
        region.replaceWith(next);
      });

      expanded.forEach((id) => {
        const group = id && document.getElementById(id);
        if (group && this.contains(group)) this.setGroupExpanded(group, true);
      });

      // Client filters: the server renders every product, re-apply the URL state before the next paint
      if (this.client) {
        this.client.state = this.clientStateFromUrl();
        this.clientWriteUrl();
        this.clientApply({ animate: false });
      }

      if (!focus) return;
      const target = focus.id ? document.getElementById(focus.id) : null;
      if (target && this.contains(target)) {
        if (focus.text !== null) target.value = focus.text;
        target.focus({ preventScroll: true });
      } else if (focus.dialog && focus.dialog.open) {
        const fallback = focus.dialog.querySelector('[data-facets-apply]') || focus.dialog.querySelector('[data-dialog-close]');
        if (fallback) fallback.focus({ preventScroll: true });
      }
    }

    replaceWhole(fresh, options) {
      this.prepareReveal(fresh);
      this.replaceWith(fresh);
      if (options.focusTab) {
        const tab = fresh.querySelector('[data-listing-full][aria-current="page"]');
        if (tab) tab.focus({ preventScroll: true });
      }
      if (typeof fresh.afterRender === 'function') {
        const count = fresh.querySelector('[data-listing-region="count"]');
        fresh.afterRender(count ? count.textContent : '', { ...options, focusSort: false });
      }
    }

    afterRender(countText, options) {
      this.announce(countText);

      if (options.focusSort) {
        const summary = this.querySelector('[data-sort] summary');
        if (summary) summary.focus({ preventScroll: true });
      }

      const main = this.querySelector('.nl-listing__main') || this;
      const top = main.getBoundingClientRect().top;
      const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nl-header-offset')) || 0;
      const outOfView = top < offset || top > window.innerHeight * 0.85;
      if ((options.scroll || (options.source === 'sidebar' && top < offset)) && outOfView) {
        window.scrollTo({
          top: Math.max(0, window.scrollY + top - offset - 24),
          behavior: reduced() ? 'auto' : 'smooth',
        });
      }

      this.dispatchEvent(
        new CustomEvent('listing:updated', { bubbles: true, detail: { url: this.current, sectionId: this.sectionId } })
      );
    }

    announce(text) {
      const status = this.querySelector('[data-listing-status]');
      if (!status) return;
      status.textContent = '';
      window.setTimeout(() => {
        status.textContent = String(text || '').replace(/\s+/g, ' ').trim();
      }, 60);
    }

    /* ---------------- Client filters (small catalogue, every product on the page) ---------------- */

    clientInit() {
      if (this.dataset.filterMode !== 'client') {
        this.client = null;
        return;
      }
      let strings = {};
      const json = this.querySelector('script[data-client-strings]');
      try {
        strings = json ? JSON.parse(json.textContent) : {};
      } catch (error) {
        strings = {};
      }
      const icon = this.querySelector('template[data-client-icon]');
      this.client = { strings, icon: icon ? icon.innerHTML : '', state: {} };
      this.client.state = this.clientStateFromUrl();
      this.clientApply({ animate: false });
    }

    clientGroups() {
      const names = new Set();
      this.querySelectorAll('[data-client-option]').forEach((input) => names.add(input.name));
      return Array.from(names);
    }

    clientStateFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const state = {};
      this.clientGroups().forEach((group) => {
        const values = params
          .getAll(group)
          .join(',')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean);
        if (values.length) state[group] = new Set(values);
      });
      return state;
    }

    clientChange(target, form) {
      if (!this.client || !target.matches('[data-client-option]')) return;
      const [group, value] = target.dataset.clientOption.split('~');
      const set = this.client.state[group] || new Set();
      if (target.checked) set.add(value);
      else set.delete(value);
      this.client.state[group] = set;
      if (form.dataset.context === 'drawer') this.drawerDirty = true;
      this.clientCommit({ source: form.dataset.context, keepFocus: target });
    }

    clientCommit(options = {}) {
      this.clientWriteUrl();
      const visible = this.clientApply({ animate: true });
      this.announce(this.clientCountText(visible));
      if (options.keepFocus && document.activeElement !== options.keepFocus && options.keepFocus.isConnected) {
        options.keepFocus.focus({ preventScroll: true });
      }
      if (options.focusAfter) {
        const target =
          this.querySelector('dialog[open] [data-facets-apply]') ||
          this.querySelector('.nl-listing__sidebar [data-client-option]:not(:disabled)') ||
          this.querySelector('[data-listing-region="count"]');
        const lost = !document.activeElement || document.activeElement === document.body;
        if (target && lost) {
          if (target.matches('h2')) target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
      }
      // Sidebar filtering from far down the page: bring the first results back into view
      if (options.source === 'sidebar') {
        const main = this.querySelector('.nl-listing__main');
        const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nl-header-offset')) || 0;
        if (main && main.getBoundingClientRect().top < offset) {
          window.scrollTo({
            top: Math.max(0, window.scrollY + main.getBoundingClientRect().top - offset - 24),
            behavior: reduced() ? 'auto' : 'smooth',
          });
        }
      }
    }

    clientWriteUrl() {
      if (designMode()) return;
      const url = new URL(window.location.href);
      const groups = this.clientGroups();
      groups.forEach((group) => url.searchParams.delete(group));
      url.searchParams.delete('page');
      const parts = [];
      const rest = url.searchParams.toString();
      if (rest) parts.push(rest);
      groups.forEach((group) => {
        const set = this.client.state[group];
        if (set && set.size) parts.push(`${group}=${Array.from(set).map(encodeURIComponent).join(',')}`);
      });
      const next = url.pathname + (parts.length ? `?${parts.join('&')}` : '') + url.hash;
      try {
        window.history.replaceState({ ...(window.history.state || {}), nlListing: true }, '', next);
      } catch (error) {
        /* sandboxed preview */
      }
      this.current = cleanUrl(window.location.href);
    }

    clientPlural(forms, count) {
      if (!forms) return String(count);
      let key = 'other';
      if (count === 1) key = 'one';
      else if (count >= 2 && count <= 4) key = 'few';
      const template = forms[key] || forms.other || '';
      return template.replace(/\d+/, String(count));
    }

    clientCountText(count) {
      return this.clientPlural(this.client && this.client.strings.count, count);
    }

    clientApply(options = {}) {
      if (!this.client) return 0;
      const state = this.client.state;
      const items = Array.from(this.querySelectorAll('[data-listing-grid] > [data-listing-item]')).map((el) => ({
        el,
        tokens: new Set((el.dataset.nlF || '').split(/\s+/).filter(Boolean)),
        available: el.dataset.available === 'true',
      }));

      const matches = (item, skip) => {
        for (const [group, set] of Object.entries(state)) {
          if (group === skip || !set || !set.size) continue;
          if (group === 'skladem') {
            if (!item.available) return false;
            continue;
          }
          let hit = false;
          for (const value of set) {
            if (item.tokens.has(`${group}~${value}`)) {
              hit = true;
              break;
            }
          }
          if (!hit) return false;
        }
        return true;
      };

      // Products
      let visible = 0;
      const shown = [];
      items.forEach((item) => {
        const on = matches(item, null);
        if (on) visible += 1;
        if (item.el.hidden === on) {
          item.el.hidden = !on;
          if (on) shown.push(item.el);
        }
      });

      // Options: checked state, live counts, impossible options disabled
      const labels = {};
      this.querySelectorAll('[data-client-option]').forEach((input) => {
        const key = input.dataset.clientOption;
        const [group, value] = key.split('~');
        const checked = Boolean(state[group] && state[group].has(value));
        input.checked = checked;
        let count = 0;
        items.forEach((item) => {
          if (!matches(item, group)) return;
          if (group === 'skladem' ? item.available : item.tokens.has(key)) count += 1;
        });
        const label = input.closest('label');
        const countEl = label && label.querySelector('[data-client-count]');
        if (countEl) countEl.textContent = String(count);
        const off = count === 0 && !checked;
        input.disabled = off;
        if (label) label.classList.toggle('is-disabled', off);
        const text = label && label.querySelector('[data-client-label]');
        if (text) labels[key] = text.textContent.trim();
      });

      // Active values (chips, badge, clear buttons)
      const active = [];
      Object.entries(state).forEach(([group, set]) => {
        if (!set) return;
        set.forEach((value) => {
          const key = `${group}~${value}`;
          if (labels[key]) active.push({ key, label: labels[key] });
        });
      });
      this.clientRenderChips(active);

      const countEl = this.querySelector('[data-listing-region="count"]');
      if (countEl) countEl.textContent = this.clientCountText(visible);
      const badge = this.querySelector('[data-listing-region="badge"]');
      if (badge) {
        badge.innerHTML = active.length
          ? `<span class="visually-hidden">, ${this.escape(this.client.strings.active || '')}:</span> ${active.length}`
          : '';
      }
      const apply = this.querySelector('[data-facets-apply]');
      if (apply) apply.textContent = this.clientPlural(this.client.strings.apply, visible);
      this.querySelectorAll('.nl-facets-drawer__footer [data-client-clear]').forEach((button) => {
        button.hidden = active.length === 0;
      });
      const empty = this.querySelector('[data-client-empty]');
      if (empty) empty.hidden = visible > 0;
      const grid = this.querySelector('[data-listing-grid]');
      if (grid) grid.hidden = visible === 0;

      if (options.animate && !reduced() && shown.length) {
        shown.slice(0, 12).forEach((el, index) => {
          if (!el.classList.contains('is-reveal-done') && el.hasAttribute('data-reveal')) return;
          if (typeof el.animate !== 'function') return;
          el.animate(
            [
              { opacity: 0, transform: 'translateY(12px) scale(0.985)' },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 380, delay: Math.min(index, 8) * 45, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'backwards' }
          );
        });
      }
      return visible;
    }

    escape(text) {
      const div = document.createElement('div');
      div.textContent = String(text);
      return div.innerHTML;
    }

    clientRenderChips(active) {
      const strings = this.client.strings;
      let html = '';
      if (active.length) {
        const chips = active
          .map((entry) => {
            const aria = (strings.remove || '%f').replace('%f', entry.label);
            return `<li><button type="button" class="chip nl-active__chip" data-client-remove="${this.escape(entry.key)}" aria-label="${this.escape(aria)}">${this.escape(entry.label)}<span class="chip__remove" aria-hidden="true">${this.client.icon}</span></button></li>`;
          })
          .join('');
        html = `<div class="nl-active"><ul class="chip-list nl-active__list" role="list" aria-label="${this.escape(strings.active || '')}">${chips}</ul><button type="button" class="nl-active__clear" data-client-clear>${this.escape(strings.clear || '')}</button></div>`;
      }
      const region = this.querySelector('[data-listing-region="active"]');
      if (region) region.innerHTML = html;
      const drawer = this.querySelector('[data-client-active]');
      if (drawer) {
        drawer.innerHTML = active.length ? html.replace('<div class="nl-active">', '').replace(/<\/div>$/, '') : '';
        drawer.hidden = active.length === 0;
      }
    }

    /* ---------------- Load more ---------------- */

    async loadMore(link) {
      if (link.getAttribute('aria-busy') === 'true') return;
      link.setAttribute('aria-busy', 'true');
      const href = link.href;

      try {
        const html = await fetchHTML(sectionUrl(cleanUrl(href), this.sectionId));
        const fresh = parse(html).querySelector('listing-results');
        const grid = this.querySelector('[data-listing-grid]');
        const items = fresh ? Array.from(fresh.querySelectorAll('[data-listing-grid] > [data-listing-item]')) : [];
        if (!grid || !items.length) throw new Error('Nothing to append');

        if (!reduced()) {
          items.forEach((item, index) => {
            item.setAttribute('data-reveal', '');
            item.setAttribute('data-reveal-delay', String((index % 4) * 70));
          });
        }
        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.append(item));
        const first = items[0];
        grid.append(fragment);

        const pagination = this.querySelector('[data-listing-region="pagination"]');
        const nextPagination = fresh.querySelector('[data-listing-region="pagination"]');
        if (pagination && nextPagination) pagination.replaceWith(nextPagination);
        this.setAttribute('data-appended', '');

        // Keyboard users continue at the first new product
        const focusTarget = first.querySelector('a[href]');
        if (focusTarget) focusTarget.focus({ preventScroll: true });

        const progress = this.querySelector('[data-listing-progress]');
        this.announce(progress ? progress.textContent : '');
        this.dispatchEvent(
          new CustomEvent('listing:updated', { bubbles: true, detail: { url: href, sectionId: this.sectionId } })
        );
      } catch (error) {
        window.location.assign(href);
      }
    }
  }

  define('listing-results', ListingResults);

  /* ==========================================================================
     <search-tab-counts>
     ========================================================================== */

  class SearchTabCounts extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;
      const endpoint = this.dataset.endpoint;
      if (!endpoint) return;

      this.querySelectorAll('[data-tab-count]').forEach((slot) => {
        if (slot.textContent.trim() !== '') {
          this.mark(slot, slot.textContent.trim());
          return;
        }
        const type = slot.dataset.tabCount;
        const url = new URL(endpoint, window.location.origin);
        if (type && type !== 'all') url.searchParams.set('type', type);
        url.searchParams.set('section_id', this.dataset.section || 'nl-search-counts');
        const key = `nl:search-count:${url.search}`;

        let stored = null;
        try {
          stored = window.sessionStorage.getItem(key);
        } catch (error) {
          stored = null;
        }
        if (stored !== null) {
          this.fill(slot, stored);
          return;
        }

        fetch(url.pathname + url.search, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
          .then((html) => {
            const node = parse(html).querySelector('[data-count]');
            const count = node ? node.getAttribute('data-count') : '';
            if (count === '') return;
            try {
              window.sessionStorage.setItem(key, count);
            } catch (error) {
              /* storage blocked */
            }
            this.fill(slot, count);
          })
          .catch(() => {});
      });
    }

    fill(slot, count) {
      slot.textContent = count;
      this.mark(slot, count);
    }

    mark(slot, count) {
      const tab = slot.closest('a');
      if (tab) tab.toggleAttribute('data-empty', Number(count) === 0);
    }
  }

  define('search-tab-counts', SearchTabCounts);

  /* ==========================================================================
     <nl-read-more>
     ========================================================================== */

  class ReadMore extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.content = this.querySelector('[data-read-more-content]');
      this.button = this.querySelector('[data-read-more-toggle]');
      if (!this.content || !this.button) return;
      this._bound = true;
      this.button.addEventListener('click', () => this.toggle());
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.measure());
        this.observer.observe(this);
      }
      this.measure();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      this._bound = false;
    }

    measure() {
      if (this.hasAttribute('data-expanded')) return;
      const overflowing = this.content.scrollHeight > this.content.clientHeight + 4;
      this.toggleAttribute('data-overflowing', overflowing);
      this.button.hidden = !overflowing;
    }

    toggle() {
      const expanded = !this.hasAttribute('data-expanded');
      const from = this.content.getBoundingClientRect().height;
      this.toggleAttribute('data-expanded', expanded);
      this.button.setAttribute('aria-expanded', String(expanded));
      const more = this.button.querySelector('[data-label-more]');
      const less = this.button.querySelector('[data-label-less]');
      if (more) more.hidden = expanded;
      if (less) less.hidden = !expanded;

      if (!reduced() && typeof this.content.animate === 'function') {
        const to = this.content.getBoundingClientRect().height;
        this.content.animate([{ height: `${from}px` }, { height: `${to}px` }], {
          duration: 420,
          easing: 'cubic-bezier(.2,.7,.2,1)',
        });
      }
      if (!expanded) {
        this.measure();
        const top = this.getBoundingClientRect().top;
        if (top < 0) this.scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
      }
    }
  }

  define('nl-read-more', ReadMore);

  /* Listing hero: the current category pill of the swipeable row starts in view (row scroll only, never the page) */
  const showCurrentCategory = (root) => {
    (root || document).querySelectorAll('.nl-listing-hero__cat-list').forEach((list) => {
      const current = list.querySelector('[aria-current="page"]');
      if (!current || list.scrollWidth <= list.clientWidth) return;
      const item = current.closest('li') || current;
      const listBox = list.getBoundingClientRect();
      const itemBox = item.getBoundingClientRect();
      const centre = itemBox.left - listBox.left + list.scrollLeft - (list.clientWidth - itemBox.width) / 2;
      list.scrollLeft = Math.max(0, centre);
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => showCurrentCategory());
  } else {
    showCurrentCategory();
  }
  document.addEventListener('shopify:section:load', (event) => showCurrentCategory(event.target));
})();
